import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'

import SummaryCardImage from '../SummaryCardImage'

import * as Analytics from 'analytics/analytics'
import TextButton from 'components/Luxkit/Button/TextButton'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import HeadingTextBlock from 'components/Luxkit/TextBlocks/HeadingTextBlock'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import ModalContext from 'contexts/ModalContext'
import noop from 'lib/function/noop'
import SearchBookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/Search/SearchBookmarkButton'
import SearchBookmarkToItineraryButton from 'tripPlanner/components/Bookmark/BookmarkButton/Search/SearchBookmarkToItineraryButton'
import GooglePlaceRatingDisplay from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRatingDisplay'
import ExploreItemDetails from 'tripPlanner/components/TripModal/ExploreItems/ExploreItemDetails'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import { ExploreItem } from 'tripPlanner/types/common'
import AboveCardCloseButton from '../Common/AboveCardCloseButton'

const Container = styled.div`
  position: relative;
  width: 100%;
  background-color: white;
  max-width: ${rem(300)};
  pointer-events: auto;
  box-shadow: ${(props) => props.theme.shadow.bottom.medium};

  ${mediaQueryUp.largeDesktop} {
    max-width: ${rem(700)};
  }
`

const ImageContainer = styled.div`
  width: 100%;

  > * {
    height: 100%;
    aspect-ratio: 16/9;
  }

  ${mediaQueryUp.largeDesktop} {
    width: 33%;
  }
`
const ContentWrapper = styled.div`
  padding: ${rem(16)};
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: start;
  gap: ${rem(8)};

  ${mediaQueryUp.largeDesktop} {
    padding: ${rem(24)};
  }
`

const MainGroup = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  ${mediaQueryUp.largeDesktop} {
    flex-direction: row-reverse;
    justify-content: space-between;
  }
`

interface Props {
  item: ExploreItem
  showCloseButton?: boolean
  onClose?: () => void
  onSave?: () => void
}

const BookmarkButtonContainer = styled(Group)`
  position: absolute;
  top: ${rem(12)};
  right: ${rem(12)};
`

function SearchOrPOISummaryCard({ item, showCloseButton, onClose, onSave = noop }: Props) {
  const { place, name, rating, userRatingCount } = item
  const { region } = place

  const showModal = useContext(ModalContext)

  const tripId = useTripId()

  const openDetailsModal = useCallback(() => {
    showModal(<ExploreItemDetails item={item} tripId={tripId} />)

    Analytics.trackClientEvent({
      action: 'click',
      subject: 'view_more_details_explore_item',
      category: AnalyticsPage.tripPlanner,
      type: 'interaction',
    })
  }, [item, tripId, showModal])

  return (
    <Container>
      {showCloseButton && (
        <AboveCardCloseButton onClick={onClose} />
      )}
      <MainGroup>
        <ImageContainer>
          <SummaryCardImage
            place={place}
            tripId={tripId}
            tripItemId={item.id}
          />
        </ImageContainer>
        <ContentWrapper>
          <Group direction="vertical" gap={8}>
            <BodyTextBlock variant="small">{region}</BodyTextBlock>

            <HeadingTextBlock lineClamp={2} variant="heading6">
              {name}
            </HeadingTextBlock>

            {rating && (
              <GooglePlaceRatingDisplay
                rating={rating}
                ratingsCount={userRatingCount}
                size="S"
              />
            )}
          </Group>

          <TextButton
            kind="tertiary"
            size="small"
            variant="default"
            outdent="left"
            nonInteractable
            onClick={openDetailsModal}
          >
            View details
          </TextButton>
        </ContentWrapper>
      </MainGroup>
      <BookmarkButtonContainer
        direction="horizontal"
        horizontalAlign="end"
        gap={8}
      >
        <SearchBookmarkToItineraryButton
          exploreItem={item}
          variant="chip"
          onSave={onSave}
          dropdownPlacement="top"
        />
        <SearchBookmarkButton exploreItem={item} onSave={onSave} />
      </BookmarkButtonContainer>
    </Container>
  )
}

export default SearchOrPOISummaryCard
